// IAStudioVideoAdmin.jsx — Suivi admin du module IA Studio Vidéo (Menu 2)
// Stats globales (vidéos générées, storyboards, crédits, revenu) + file d'attente des
// commandes de crédits (MVola/Orange/Carte, déclaration manuelle -> validation admin).

import React, { useState, useEffect, useCallback } from 'react';
import {
  Film, Loader2, RefreshCw, CheckCircle, XCircle, Wallet, Clapperboard,
  Coins, TrendingUp, Users, Clock, Layers, Video,
} from 'lucide-react';
import { API_URL } from '../../config';

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json',
});

const METHOD_LABELS = { mvola: 'MVola', orange: 'Orange Money', card: 'Carte bancaire' };

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtTs = (ts) => ts ? new Date(ts * 1000).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

function PendingCreditOrders({ basePath, title, onValidated }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ia-studio/${basePath}/admin/credit-orders?status=pending`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setOrders(data.orders);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [basePath]);

  useEffect(() => { load(); }, [load]);

  const validate = async (id) => {
    setActingOn(id);
    try {
      await fetch(`${API_URL}/api/ia-studio/${basePath}/admin/credit-orders/${id}/validate`, { method: 'POST', headers: authHeaders() });
      await load();
      if (onValidated) onValidated();
    } finally { setActingOn(null); }
  };

  const reject = async (id) => {
    const note = window.prompt('Raison du rejet (optionnel) :') || '';
    setActingOn(id);
    try {
      await fetch(`${API_URL}/api/ia-studio/${basePath}/admin/credit-orders/${id}/reject`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ note }),
      });
      await load();
    } finally { setActingOn(null); }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-bold text-sm flex items-center gap-2">
          <Wallet className="w-4 h-4 text-emerald-400" /> {title}
          {orders.length > 0 && (
            <span className="text-[10px] bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full font-bold">{orders.length}</span>
          )}
        </h3>
        <button onClick={load} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-gray-300">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {loading ? (
        <Loader2 className="w-6 h-6 text-purple-400 animate-spin mx-auto my-6" />
      ) : orders.length === 0 ? (
        <p className="text-gray-500 text-xs text-center py-4">Aucune commande en attente — tout est validé ! 🎉</p>
      ) : (
        <div className="space-y-2">
          {orders.map(o => (
            <div key={o.$id} className="flex items-center justify-between gap-3 bg-black/20 rounded-xl p-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-white text-sm font-medium">{o.user_email || o.user_id}</div>
                <div className="text-gray-400 text-xs">
                  {METHOD_LABELS[o.method] || o.method}
                  {o.payment_ref && <> · Réf. {o.payment_ref}</>} · {fmtDate(o.created_at)}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-emerald-300 font-bold text-sm">{(o.amount_ar || 0).toLocaleString('fr-FR')} Ar</span>
                <button
                  onClick={() => validate(o.$id)}
                  disabled={actingOn === o.$id}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-white text-xs font-medium disabled:opacity-50"
                >
                  {actingOn === o.$id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />} Valider
                </button>
                <button
                  onClick={() => reject(o.$id)}
                  disabled={actingOn === o.$id}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20 text-xs font-medium disabled:opacity-50"
                >
                  <XCircle className="w-3.5 h-3.5" /> Rejeter
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color, isText = false }) => (
  <div className={`bg-gradient-to-br ${color} rounded-xl p-4 text-white`}>
    <Icon className="w-6 h-6 opacity-80 mb-2" />
    <div className={`font-bold mb-1 ${isText ? 'text-lg' : 'text-2xl'}`}>{value}</div>
    <div className="text-white/80 text-xs">{label}</div>
  </div>
);

export default function IAStudioVideoAdmin() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ia-studio/video/admin/stats`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setStats(data.stats);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const revenue = stats ? (stats.orders?.validated?.amount_ar || 0) : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Film className="w-7 h-7 text-fuchsia-400" />
            IA Studio (Image + Vidéo)
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Statistiques du module vidéo, et commandes de crédits en attente pour les deux portefeuilles indépendants (Image / Vidéo) — validation manuelle pour le moment
          </p>
        </div>
        <button
          onClick={loadStats}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {loading && !stats ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-fuchsia-400 animate-spin" />
        </div>
      ) : stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Video} label="Vidéos générées" value={stats.videos_total} color="from-fuchsia-600 to-purple-700" />
            <StatCard icon={Layers} label="Storyboards" value={`${stats.storyboards_total} (${stats.storyboard_scenes_total} scènes)`} color="from-indigo-600 to-blue-600" isText />
            <StatCard icon={Clapperboard} label="Films assemblés" value={stats.storyboards_completed} color="from-emerald-600 to-teal-600" />
            <StatCard icon={Users} label="Comptes avec crédits" value={stats.users_with_credits} color="from-pink-600 to-rose-600" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Coins} label="Soldes restants" value={stats.credits_balance_total} color="from-amber-600 to-orange-600" />
            <StatCard icon={TrendingUp} label="Crédits vendus (total)" value={stats.credits_bought_total} color="from-violet-600 to-fuchsia-700" />
            <StatCard icon={Coins} label="Crédits utilisés" value={stats.credits_used_total} color="from-cyan-600 to-blue-700" />
            <StatCard icon={TrendingUp} label="Revenu validé" value={`${revenue.toLocaleString('fr-FR')} Ar`} color="from-green-600 to-emerald-700" isText />
          </div>

          {Object.keys(stats.videos_by_provider || {}).length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <h3 className="text-white font-bold text-sm mb-3">Vidéos par style</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.videos_by_provider).map(([k, v]) => (
                  <span key={k} className="px-3 py-1.5 rounded-lg bg-white/10 text-purple-200 text-xs font-medium">{k} · {v}</span>
                ))}
              </div>
            </div>
          )}

          <PendingCreditOrders basePath="video" title="Vidéo — Commandes de crédits en attente" onValidated={loadStats} />
          <PendingCreditOrders basePath="image" title="Image — Commandes de crédits en attente" onValidated={loadStats} />

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" /> Dernières vidéos
              </h3>
              {(stats.recent_videos || []).length === 0 ? (
                <p className="text-gray-500 text-xs">Aucune vidéo générée pour l&apos;instant.</p>
              ) : (
                <div className="space-y-1.5">
                  {stats.recent_videos.map((v, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-black/15 rounded-lg px-3 py-2">
                      <span className="text-gray-300">{v.provider} · {v.resolution}</span>
                      <span className="text-gray-500">{fmtTs(v.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                <Layers className="w-4 h-4 text-gray-400" /> Derniers storyboards
              </h3>
              {(stats.recent_storyboards || []).length === 0 ? (
                <p className="text-gray-500 text-xs">Aucun storyboard pour l&apos;instant.</p>
              ) : (
                <div className="space-y-1.5">
                  {stats.recent_storyboards.map(sb => (
                    <div key={sb.storyboard_id} className="flex items-center justify-between text-xs bg-black/15 rounded-lg px-3 py-2">
                      <span className="text-gray-300 truncate mr-2">
                        {sb.title} · {sb.scene_count} scène(s){sb.has_film ? ' · 🎬' : ''}
                      </span>
                      <span className="text-gray-500 flex-shrink-0">{fmtDate(sb.updated_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
